import * as allTypes from '../mutation-type';
import * as types from '@/store/modules/recommandations-mutation-type';
import { exportPdf } from '@/api/recommandation/index';
import { getAllProduitLocations } from '@/api/produit';
import { deleteAllLogos } from '@/services/general/indexDbLogoService';
import devisToRecommandation, { lignesCommandeToPanneaux } from '@/services/recommandation/devisToRecommandation';
import createOrUpdateRecommandationDevis, {
  genererDefautDevis,
  panneauToLignesCommande,
} from '@/services/recommandation/recommandationToDevis';
import { CATEGORIE_LOCATION } from '@/configuration/produitConf';
import { REGROUPEMENT } from '@/configuration/devisConf';
import { OPTION, SANS_SUITE } from '@/configuration/optionBlocage/optionBlocage';
import { PARAMETRES_KEYS } from '@/store/modules/parametrage-mutation-type';
import gestionPanneauSousContrat from '@/services/gestionPanneau/gestionPanneauSousContrat';
import calculCommandeDevis from '@/services/devisCommandes/calcul/calculCommandeDevis';
import genererLigneCommandeHonoraireForfaitaire from '@/services/devis/genererLigneCommandeHonoraireForfaitaire';
import creerOrMajIntervenantDevis from '@/services/devis/ajoutIntervenant/addIntervenant';
import calculerThemeRecommandation from '@/services/recommandation/themeRecommandation';

function initialState() {
  return {
    devis: null,
    panneauxSousContrat: [],
    pointDeVenteAdresse: null,
    markerPanneau: null,
    activeTabListePanneau: 0,
    produitsLocation: { items: [], lastUpdate: 0 },
    panneauExistant: null,
  };
}

const state = initialState();

const namespaced = true;

const mutations = {
  [allTypes.RESET_STATE](state) {
    const s = initialState();
    Object.keys(state).forEach(key => {
      state[key] = s[key];
    });
  },
  [types.SET_POINT_DE_VENTE_ADRESSE](state, value) {
    state.pointDeVenteAdresse = value;
  },
  [types.SET_MARKER_PANNEAU](state, value) {
    state.markerPanneau = value;
  },
  [types.SET_RECOMMANDATION](state, { devis, panneauxSousContrat }) {
    state.devis = devis;
    state.panneauxSousContrat = panneauxSousContrat || [];
  },
  [types.SET_POINT_DE_VENTE](state, value) {
    if (!state.devis) return;
    state.devis.intervenants = creerOrMajIntervenantDevis(state.devis, value);
  },
  [types.SET_ASSISTANT_ID](state, value) {
    if (state.devis) {
      state.devis.assistantId = value;
    }
  },
  [types.SET_PANNEAUX_SOUS_CONTRAT](state, panneaux) {
    state.panneauxSousContrat = panneaux;
  },
  [types.SET_LIGNES_COMMANDE](state, lignesCommande) {
    if (!state.devis) return;
    state.devis.lignesCommande = lignesCommande;
    calculCommandeDevis(state.devis);
  },
  [types.SET_LIGNE_COMMANDE_PANNEAU_TO_DEVIS](state, lignesCommande) {
    if (!state.devis || !lignesCommande.length) return;
    const panneauGuid = lignesCommande[0].produit.guid;
    state.devis.lignesCommande = state.devis.lignesCommande
      .filter(l => !l.produit || l.produit.guid !== panneauGuid);
    state.devis.lignesCommande.push(...lignesCommande);
    calculCommandeDevis(state.devis);
  },
  [types.DELETE_RESTOR_PANNEAU](state, panneau) {
    const newActif = !panneau.actif;
    const etat = newActif ? OPTION : SANS_SUITE;

    if (panneau.estPanneauSousContrat) {
      const psc = state.panneauxSousContrat.find(p => p.guid === panneau.guid);
      if (psc) psc.actif = newActif;
      return;
    }

    if (!state.devis) return;
    state.devis.lignesCommande
      .filter(lc => lc.type === REGROUPEMENT && lc.produit && lc.produit.guid === panneau.guid)
      .forEach(lc => {
        lc.etatObtenu = etat;
        lc.etatSouhaite = etat;
        if (lc.lignesCommande) {
          lc.lignesCommande.forEach(child => {
            child.etatObtenu = etat;
            child.etatSouhaite = etat;
          });
        }
      });
    calculCommandeDevis(state.devis);
  },
  [types.SET_ACTIVE_TAB](state, value) {
    state.activeTabListePanneau = value;
  },
  [types.SET_PRODUITS_LOCATION](state, items) {
    state.produitsLocation = { items, lastUpdate: Date.now() };
  },
  [types.SET_PANNEAU_EXISTANT](state, panneau) {
    state.panneauExistant = panneau;
  },
  [types.CALCULER_THEME](state, payload) {
    if ((!state.devis || state.devis.theme) && (!payload || !payload.force)) return;
    state.devis.theme = calculerThemeRecommandation(state.devis);
  },
};

const actions = {
  resetStore: async ({ commit }) => {
    commit(allTypes.RESET_STATE);
    commit(`pointDeVente/${allTypes.RESET_STATE}`, null, { root: true });
    await deleteAllLogos();
  },
  initialierRecommandationDevis: async ({ commit, dispatch }, payload) => {
    await dispatch('resetStore');
    await dispatch('loadProduitsLocationForce');
    if (payload && payload.devisId) {
      const { devisId, isCopy } = payload;
      const { devis, panneauxSousContrat } = await devisToRecommandation(devisId, isCopy);
      commit(types.SET_RECOMMANDATION, { devis, panneauxSousContrat });
      if (payload.isCopy) await dispatch('loadPanneauxSousContrat');
    } else {
      const devis = genererDefautDevis();
      commit(types.SET_RECOMMANDATION, { devis, panneauxSousContrat: [] });
    }
  },
  sauvegarderRecommandationDevis: async ({ state, dispatch }) => {
    await dispatch('parametrage/loadParametres', [PARAMETRES_KEYS.materielFormat], { root: true });
    const devisResult = await createOrUpdateRecommandationDevis(
      state.devis,
      state.panneauxSousContrat,
    );
    await dispatch('resetStore');
    return devisResult;
  },
  loadPanneauxSousContrat: async ({ state, commit }) => {
    if (!state.devis) return;
    const intervenantAnnonceur = state.devis.intervenants
      && state.devis.intervenants.find(i => i.annonceur);
    const pointDeVenteId = intervenantAnnonceur ? intervenantAnnonceur.tiersId : null;
    if (!pointDeVenteId) return;
    const recommandationCompat = {
      id: state.devis.id,
      panneauxSousContrat: state.panneauxSousContrat,
      pointDeVente: { id: pointDeVenteId },
    };
    const panneaux = await gestionPanneauSousContrat(recommandationCompat);
    commit(types.SET_PANNEAUX_SOUS_CONTRAT, panneaux);
  },
  getPdfRecom: async (_, id) => {
    const resp = await exportPdf(id);
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(resp.data);
    const disposition = resp.request.getResponseHeader('Content-Disposition');
    link.download = decodeURI(disposition.match(/filename\*=UTF-8''(.*?)$/)[1]);
    link.click();
    return true;
  },
  loadProduitsLocation: async ({ state, dispatch }) => {
    const { lastUpdate } = state.produitsLocation;
    const dateLimit = Date.now() - 24 * 60 * 60 * 1000;
    if (lastUpdate && lastUpdate > dateLimit) return;
    await dispatch('loadProduitsLocationForce')
    return;
  },
  loadProduitsLocationForce: async ({ commit, rootGetters }) => {
    const societeId = rootGetters['societe/societeActiveId'];
    if (!societeId) return;
    const res = (await getAllProduitLocations({
      query: {
        categorie: [CATEGORIE_LOCATION],
        nonCommercialisable: false,
        societeId,
        actif: true,
      },
      maxResultCount: 20,
      sorting: 'CreationTime DESC',
    })).data.result.items;
    commit(types.SET_PRODUITS_LOCATION, res);
    return;
  },
  setLigneHonoraireForfaitaire: ({ state, commit }, montant) => {
    if (!state.devis) return;
    const lignesCommande = genererLigneCommandeHonoraireForfaitaire(state.devis, montant);
    commit(types.SET_LIGNES_COMMANDE, lignesCommande);
  },
  setPanneauToDevis: async ({ state, commit }, panneau) => {
    if (!state.devis) return;
    const lignesCommande  = await panneauToLignesCommande(panneau, state.devis.lignesCommande);
    commit(types.SET_LIGNE_COMMANDE_PANNEAU_TO_DEVIS, lignesCommande);
  },
};

const getters = {
  pointDeVenteAdresse: (state) => {
    return state.pointDeVenteAdresse;
  },
  markerPanneau: (state) => {
    return state.markerPanneau;
  },
  panneauExistant: (state) => {
    return state.panneauExistant;
  },
  recommandation: (state) => {
    return state.devis;
  },
  pointDeVente: (state) => {
    if (!state.devis || !state.devis.intervenants) return null;
    const intervenantAnnonceur = state.devis.intervenants.find(i => i.annonceur);
    return intervenantAnnonceur ? intervenantAnnonceur.tiers : null;
  },
  assistant: (state) => {
    if (!state.devis) return null;
    return state.devis.assistantId;
  },
  panneaux: (state, getters, rootState, rootGetters) => {
    if (!state.devis) return [];
    const societe = rootGetters['societe/societeActive'];
    const lignes = lignesCommandeToPanneaux(state.devis.lignesCommande, societe, state.devis.issueRenouvellement);
    if (!state.devis.afficherPanneauxSousContrat) return lignes;
    return [...lignes, ...state.panneauxSousContrat];
  },
  activeTabListePanneau(state) {
    return state.activeTabListePanneau;
  },
  honoraireForfaitaire: (state, getters, rootState, rootGetters) => {
    if (!state.devis) return null;
    const societe = rootGetters['societe/societeActive'];
    if (!societe) return null;
    const ligne = state.devis.lignesCommande.find(
      lc => lc.produitId === societe.produitIdHonoraireForfaitaire,
    );
    return ligne ? ligne.prixUnitaire : null;
  },
  produitsLocation(state) {
    return state.produitsLocation.items;
  },
};

export default {
  namespaced,
  state,
  mutations,
  actions,
  getters,
};
